<template>
	<section class="v2t">

		<!-- ════════ HERO BANNER: CAMPAIGN TYPE ════════ -->
		<div class="v2t__hero" :class="{ 'v2t__hero--purple': isGuaranteed }">
			<div class="v2t__hero-icon">
				<svg v-if="isGuaranteed" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
					<path d="M12 2L4 5v6c0 4.5 3.4 8.5 8 10 4.6-1.5 8-5.5 8-10V5l-8-3z"/>
					<path d="M9 12l2 2 4-4"/>
				</svg>
				<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="6 3 20 12 6 21 6 3"/></svg>
			</div>
			<span class="v2t__hero-text">Campaign type is</span>
			<span v-if="isGuaranteed" class="v2t__hero-pill">{{ $t('payment_models.guaranteed') }}</span>
			<select
				v-else-if="enabledPaymentModels.length > 1"
				v-model="instance.payment_model"
				class="v2t__hero-select"
			>
				<option
					v-for="model in enabledPaymentModels"
					:key="model"
					:value="model"
				>{{ $t(`payment_models.${model}`) }}</option>
			</select>
			<span v-else class="v2t__hero-model">{{ $t(`payment_models.${instance.payment_model}`) }}</span>
			<span class="v2t__hero-hint">
				<svg v-if="isGuaranteed" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
				<svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
				{{ isGuaranteed ? $t('guaranteed.hero_hint_back_to_auction') : 'You can change the pricing model in step 1' }}
			</span>
		</div>

		<!-- ════════ SCHEDULE ════════ -->
		<div class="v2t__card">
			<div class="v2t__section-header">
				<div class="v2t__section-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4"/><path d="M3 10h18"/><path d="M8 2v4"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">Schedule</h3>
					<p class="v2t__section-sub">{{ isGuaranteed ? 'Campaign run window. Per-day time ranges live in Timing Rules below.' : 'When should the campaign run?' }}</p>
				</div>
			</div>

			<div class="v2t__schedule-cols">
				<div class="v2t__schedule-col">
					<label class="v2t__label">Start Date &amp; Time *</label>
					<NiceDatetime
						v-model="startDate"
						only-date
						:min-date="minDate"
						format="YYYY-MM-DD"
						:formatted="getDateFormat()"
						placeholder="Select date"
						no-shortcuts
						auto-close
						class="v2t__datetime"
						required
					/>
					<input
						:value="startTimeFormatted"
						type="time"
						class="v2t__input v2t__input--time"
						@input="updateStartTime($event.target.value)"
					/>
				</div>
				<div class="v2t__schedule-sep">&mdash;</div>
				<div class="v2t__schedule-col">
					<label class="v2t__label">End Date &amp; Time *</label>
					<NiceDatetime
						v-model="endDate"
						only-date
						:min-date="minDate"
						format="YYYY-MM-DD"
						:formatted="getDateFormat()"
						placeholder="Select date"
						no-shortcuts
						auto-close
						class="v2t__datetime"
						required
					/>
					<input
						:value="endTimeFormatted"
						type="time"
						class="v2t__input v2t__input--time"
						@input="updateEndTime($event.target.value)"
					/>
				</div>
			</div>

			<div v-if="!isGuaranteed" class="v2t__weekdays">
				<label class="v2t__label">Week Days</label>
				<div class="v2t__pill-group">
					<button
						v-for="day in weekDays"
						:key="day.key"
						type="button"
						class="v2t__pill"
						:class="{ 'v2t__pill--active': instance.schedule && instance.schedule[day.key] }"
						@click="toggleDay(day.key)"
					>{{ day.label }}</button>
				</div>
			</div>
			<p v-else class="v2t__help v2t__help--mt">{{ $t('guaranteed.schedule_replaced_by_timing_rules') }}</p>
		</div>

		<!-- ════════ AD DURATION & FREQUENCY (or Spot Duration in Guaranteed) ════════ -->
		<div class="v2t__card">
			<div class="v2t__section-header">
				<div class="v2t__section-icon">
					<svg v-if="isGuaranteed" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
					<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">{{ isGuaranteed ? $t('guaranteed.spot_duration') : 'Ad Duration & Frequency' }}</h3>
					<p class="v2t__section-sub">{{ isGuaranteed ? $t('guaranteed.spot_duration_hint') : 'Control playback length and capping' }}</p>
				</div>
			</div>

			<div class="v2t__adfreq">
				<div class="v2t__adfreq-col">
					<label class="v2t__label">{{ isGuaranteed ? 'Duration *' : 'Message Duration *' }}</label>
					<div class="v2t__input-wrap v2t__input-wrap--duration">
						<input
							v-model.number="instance.ad_duration"
							type="number"
							min="1"
							max="300"
							step="1"
							class="v2t__input v2t__input--has-suffix-sm"
							:class="{ 'v2t__input--error': context.errors.ad_duration }"
						/>
						<span class="v2t__input-suffix-sm">SEC</span>
					</div>
					<p class="v2t__help">{{ isGuaranteed ? $t('guaranteed.spot_duration_help') : 'Length of one playback' }}</p>
				</div>
				<div v-if="!isGuaranteed" class="v2t__adfreq-col">
					<label class="v2t__label">Frequency Limit</label>
					<div class="v2t__freq-inputs">
						<div class="v2t__input-wrap v2t__input-wrap--freq">
							<input
								v-model.number="instance.ad_rate_per_hour"
								type="number"
								min="1"
								max="720"
								step="1"
								class="v2t__input"
								:class="{ 'v2t__input--error': context.errors.ad_rate_per_hour }"
							/>
						</div>
						<select
							v-model="instance.ad_rate_multiplier"
							class="v2t__select"
						>
							<option
								v-for="opt in adRateOptions"
								:key="opt.value"
								:value="opt.value"
							>{{ opt.label }}</option>
						</select>
					</div>
				</div>
			</div>
		</div>

		<!-- ════════ BUDGET & BIDDING (Auction only) ════════ -->
		<div v-if="!isGuaranteed" class="v2t__card">
			<div class="v2t__section-header">
				<div class="v2t__section-icon v2t__section-icon--accent">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">Budget &amp; Bidding</h3>
					<p class="v2t__section-sub">Set spend and bidding strategy</p>
				</div>
			</div>

			<div class="v2t__grid-2">
				<!-- Left: Budget + Max Bid -->
				<div class="v2t__stack">
					<div>
						<label class="v2t__label">Total Budget *</label>
						<div class="v2t__input-wrap v2t__input-wrap--budget">
							<input
								ref="budget"
								v-model.number="instance.budget"
								type="number"
								min="0.0001"
								step="0.0001"
								class="v2t__input v2t__input--has-suffix"
								:class="{ 'v2t__input--error': context.errors.budget }"
								@change="budgetChangeHandler"
							/>
							<span class="v2t__input-suffix">{{ instance.currency | formatCurrency }}</span>
						</div>
						<p class="v2t__help">
							Estimated max for this line order:
							<b v-if="currentSelection" v-html="$options.filters.formatCash(currentSelection.total_max_budget, instance.currency)" />
							<b v-else>$0</b>
						</p>
						<div
							v-if="instance.owner_instance && instance.budget > instance.owner_instance.balance"
							class="v2t__help v2t__help--error"
						>{{ $t('campaign.budget_more_than_balance') }}</div>
					</div>
					<div>
						<label class="v2t__label">Max Bid (per play)</label>
						<div class="v2t__input-wrap v2t__input-wrap--budget">
							<input
								v-if="!instance.is_fixed_placement"
								v-model.number="instance.max_bid"
								type="number"
								min="0.0001"
								step="0.0001"
								class="v2t__input v2t__input--has-suffix"
								:class="{ 'v2t__input--error': context.errors.max_bid }"
								@change="maxBidChangeHandler"
							/>
							<input
								v-else
								:value="$options.filters.formatNumber(instance.max_bid) || '—'"
								class="v2t__input v2t__input--has-suffix"
								disabled
							/>
							<span class="v2t__input-suffix">{{ instance.currency | formatCurrency }}</span>
						</div>
						<p
							v-if="currentSelection && !instance.is_fixed_placement"
							class="v2t__help"
							v-html="'Range: ' + $options.filters.formatCash(currentSelection.price.min, instance.currency, 2) + ' — ' + $options.filters.formatCash(currentSelection.price.max, instance.currency, 2)"
						/>
					</div>
				</div>

				<!-- Right: Fixed Placement toggle + Fixed Cost (shown when ON) -->
				<div class="v2t__stack">
					<div
						class="v2t__toggle-box"
						:class="{ 'v2t__toggle-box--on': instance.is_fixed_placement }"
					>
						<div class="v2t__toggle-row">
							<div>
								<p class="v2t__toggle-title">Fixed Placement</p>
								<p class="v2t__toggle-desc">Guarantee your placement at fixed price</p>
							</div>
							<label class="v2t__switch">
								<input
									v-model="instance.is_fixed_placement"
									type="checkbox"
									@change="instance.max_bid = currentSelection ? currentSelection.price.max : 0"
								/>
								<span class="v2t__switch-slider" />
							</label>
						</div>
					</div>

					<div v-if="instance.is_fixed_placement" class="v2t__fixed-cost-box">
						<label class="v2t__label">Fixed Cost</label>
						<p class="v2t__fixed-cost-value">
							{{ $options.filters.formatNumber(instance.max_bid) || '—' }}
							<span class="v2t__fixed-cost-currency">{{ instance.currency | formatCurrency }}</span>
						</p>
					</div>
				</div>
			</div>

			<p v-if="currentSelection && currentPaymentModel" class="v2t__help" style="margin-top: 12px;">
				{{ currentPaymentModel.label }}
				{{ $t('campaign.model_phrase', { seconds: $tc('campaign.seconds', instance.ad_duration) }) }}
			</p>
		</div>

		<!-- ════════ ADDRESS + MAP ════════ -->
		<div class="v2t__map-grid">

			<!-- Address Card -->
			<div class="v2t__card v2t__address-card">
				<div class="v2t__section-header">
					<div class="v2t__section-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
					</div>
					<div>
						<h3 class="v2t__section-title">Address</h3>
						<p class="v2t__section-sub">Target specific locations</p>
					</div>
				</div>

				<SelectAreas
					v-model="instance.areas"
					:search-list-request="searchListRequest"
					:search-query.sync="searchQuery"
					:error="context.errors.areas"
					class="v2t__select-areas"
				/>
			</div>

			<!-- Map Card -->
			<div class="v2t__map-wrap">
				<MapDevice
					:source-data="mapSource"
					:selected-areas-data="instance.areas"
					enable-geocoder
					:search-query="searchQuery"
					:campaign-id="context.instance.id"
					:without-control="!instance.platforms || instance.platforms.length == 0"
					class="v2t__map"

					@update="updateMapAndStats"
					@searchListRequest="searchlistRequestHandler"
					@update:selectedAreasData="updateSelectedAreas"
				/>
			</div>
		</div>

		<!-- ════════ DEVICE CHOICES (Broadcasting Points + Audience) ════════ -->
		<div
			v-show="showDeviceChoices"
			class="v2t__card"
		>
			<div class="v2t__section-header">
				<div class="v2t__section-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">Broadcasting Points &amp; Audience</h3>
					<p class="v2t__section-sub">Filter devices and target your audience</p>
				</div>
			</div>

			<DeviceChoices
				:key="`devicechoices-${deviceChoicesKey}`"
				:payment-model="instance.payment_model"
				:platforms="instance.platforms"
				:age.sync="instance.age"
				:gender.sync="instance.gender"
				:environments.sync="instance.environments"
				:pois.sync="instance.pois"
				:resolutions.sync="instance.resolutions"
				:orientations.sync="instance.orientations"
				:device-types.sync="instance.device_types"
				class="v2t__device-choices"

				@lists-loaded="onDeviceChoicesListsLoaded"
			/>
		</div>

		<!-- ════════ PLATFORMS ════════ -->
		<div v-if="networks.length > 1" class="v2t__card">
			<div class="v2t__section-header">
				<div class="v2t__section-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">Platforms</h3>
					<p class="v2t__section-sub">Choose advertising networks to target</p>
				</div>
			</div>

			<nice-spoiler
				:label="$t('campaign.all_platforms_label') | capitalize"
				:expand="isPlatformsSpoilerExpanded"
				:askConfirmation="true"
				:askConfirmationText="$t('campaign.reset_platforms_selection_confirmation_text')"
				class="v2t__platforms-spoiler"
				@collapse="resetPlatforms"
			>
				<network-tree-check
					v-if="isMultiSPPMode"
					:platforms="instance.platforms"
					:networks="networks"
					@update:platforms="updatePlatforms"
				/>
				<nice-tree-check
					v-else
					v-model="instance.platforms"
					:tree="networks"
				/>
			</nice-spoiler>
			<p class="v2t__help" v-html="$t('campaign.selected_platforms', { num: instance.platforms.length })" />
		</div>

		<!-- ════════ TIMING RULES (Guaranteed only) ════════ -->
		<div v-if="isGuaranteed" class="v2t__card v2t__card--accent">
			<div class="v2t__section-header">
				<div class="v2t__section-icon v2t__section-icon--accent">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">{{ $t('guaranteed.timing_rules') }}</h3>
					<p class="v2t__section-sub">{{ $t('guaranteed.timing_rules_hint') }}</p>
				</div>
			</div>

			<GuaranteedTimingRules
				:value="instance.timing_rules || []"
				@input="onTimingRulesInput"
			/>
		</div>

		<!-- ════════ PLAYBACK RULES (Guaranteed only) ════════ -->
		<div v-if="isGuaranteed" class="v2t__card v2t__card--accent">
			<div class="v2t__section-header">
				<div class="v2t__section-icon v2t__section-icon--accent">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="6 3 20 12 6 21 6 3"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">{{ $t('guaranteed.playback_rules') }}</h3>
					<p class="v2t__section-sub">{{ $t('guaranteed.playback_rules_hint') }}</p>
				</div>
			</div>

			<GuaranteedPlaybackRules
				:value="playbackRules || { content_show_count: 1, playback_order: 2 }"
				:book-without-content="!!instance.book_without_content"
				@input="onPlaybackRulesInput"
				@update:bookWithoutContent="onBookWithoutContentInput"
			/>
		</div>

		<!-- ════════ CAMPAIGN SUMMARY ════════ -->
		<div class="v2t__summary-card">
			<div class="v2t__section-header">
				<div class="v2t__section-icon v2t__section-icon--accent">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M7 16h8"/><path d="M7 11h12"/><path d="M7 6h3"/></svg>
				</div>
				<div>
					<h3 class="v2t__section-title">Campaign Summary</h3>
					<p class="v2t__section-sub">{{ isGuaranteed ? 'Live estimate based on selected screens and timing rules.' : 'Live estimates based on your configuration' }}</p>
				</div>
			</div>

			<!-- Guaranteed-specific stats -->
			<div v-if="isGuaranteed" class="v2t__stats-grid">
				<div class="v2t__stat">
					<p class="v2t__stat-label">Pricing Model</p>
					<p class="v2t__stat-value v2t__stat-value--text v2t__stat-value--accent">{{ $t('payment_models.guaranteed') }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Days Active</p>
					<p class="v2t__stat-value v2t__stat-value--primary">{{ adFlight || '—' }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Devices</p>
					<p class="v2t__stat-value">{{ currentSelection && currentSelection.inventory ? (currentSelection.inventory.value || 0) : 0 }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">{{ $t('guaranteed.summary_active_days_per_week') }}</p>
					<p class="v2t__stat-value">{{ guaranteedActiveDays }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">{{ $t('guaranteed.summary_time_ranges') }}</p>
					<p class="v2t__stat-value">{{ guaranteedTotalRanges }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Spot Duration</p>
					<p class="v2t__stat-value">{{ instance.ad_duration || 0 }}<span class="v2t__stat-value-unit">SEC</span></p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">{{ $t('guaranteed.summary_reserved_only') }}</p>
					<p class="v2t__stat-value v2t__stat-value--text v2t__stat-value--accent">{{ instance.book_without_content ? $t('guaranteed.summary_reserved_yes') : $t('guaranteed.summary_reserved_no') }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">{{ $t('campaign.budget') }}</p>
					<p class="v2t__stat-value v2t__stat-value--text v2t__stat-value--accent">{{ instance.budget }}</p>
				</div>
			</div>

			<!-- Auction-mode stats (unchanged) -->
			<div v-else-if="currentSelection" class="v2t__stats-grid">
				<div class="v2t__stat">
					<p class="v2t__stat-label">Pricing Model</p>
					<p class="v2t__stat-value v2t__stat-value--text">{{ $t(`payment_models.${instance.payment_model}`) }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Days</p>
					<p class="v2t__stat-value v2t__stat-value--primary">{{ adFlight || '—' }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Per Day</p>
					<p class="v2t__stat-value" :class="{ 'v2t__stat-value--danger': !currentSelection.plays_per_day }">{{ currentSelection.plays_per_day || 0 }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Total Plays</p>
					<p class="v2t__stat-value" :class="{ 'v2t__stat-value--danger': !currentSelection.plays.value }">{{ currentSelection.plays.value || 0 }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Selected</p>
					<p class="v2t__stat-value" :class="{ 'v2t__stat-value--danger': !currentSelection.inventory.value }">{{ currentSelection.inventory.value || 0 }}</p>
				</div>
				<div class="v2t__stat">
					<p class="v2t__stat-label">Available</p>
					<p class="v2t__stat-value">{{ currentSelection.inventory.total || 0 }}</p>
				</div>
			</div>

			<div class="v2t__summary-cta">
				<button
					type="button"
					class="v2t__btn v2t__btn--outline"
					:disabled="!instance.platforms || instance.platforms.length == 0"
					@click="isInventoryModalShowing = true"
				>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M7 16h8"/><path d="M7 11h12"/><path d="M7 6h3"/></svg>
					{{ $t('campaign.open_inventory_modal') }}
				</button>
			</div>
		</div>

		<!-- ════════ VALIDATION MESSAGES ════════ -->
		<div v-if="isAnyRequredFieldEmpty || isAnyErrorsFromBackend" class="v2t__validation">
			<span v-if="isAnyRequredFieldEmpty" class="v2t__validation-msg">
				{{ $t('errors.fill_required_fields') }}
			</span>
			<span v-if="isAnyErrorsFromBackend" class="v2t__validation-msg v2t__validation-msg--error">
				{{ $t('errors.resolve_errors') }}
			</span>
		</div>

		<!-- ════════ INVENTORY MODAL ════════ -->
		<InventoryDetailsModal
			v-if="isInventoryModalShowing"
			class="v2t__inventory-modal"
			:instance="context.instance"
			:current-selection="currentSelection"
			:showAdditionalValue="true"
			:playsPerDay="currentSelection ? currentSelection.plays_per_day : 0"
			:devicesUptime="currentSelection ? currentSelection.devices_uptime : {}"
			@update-required="updateStats"
			@close="isInventoryModalShowing = false"
		/>

	</section>
</template>


<script>
import moment from 'moment';
import Step03 from '@/ui-overrides/default/pages/request/create/step_03.vue';
import NiceDatetime from '@/ui/nice-datetime.vue';
import V2InventoryDetailsModal from './inventory-details-modal/index.vue';
import GuaranteedTimingRules from './guaranteed/timing-rules.vue';
import GuaranteedPlaybackRules from './guaranteed/playback-rules.vue';
import { PAYMENT_MODEL_GUARANTEED } from '@/constants/payment_models';

import { getDateFormat } from '@/utilites';

export default {
	name: 'V2StepTargeting',

	extends: Step03,

	components: {
		...Step03.components,
		NiceDatetime,
		InventoryDetailsModal: V2InventoryDetailsModal,
		GuaranteedTimingRules,
		GuaranteedPlaybackRules,
	},

	data() {
		return {
			...Step03.data.call(this),

			showAdvancedSettings: true,
			showDeviceChoices: true,

			// TODO: other fields?
			requiredFields: [
				// 'start_date',
				// 'end_date',
				'time_period',
				'time_range',
				'ad_duration',
				'ad_rate_per_hour',
				// 'payment_model',
				// not needed for guaranteed
				// 'budget',
				// 'max_bid',
			],
		};
	},

	computed: {
		isGuaranteed() {
			return this.instance && this.instance.payment_model === PAYMENT_MODEL_GUARANTEED;
		},

		guaranteedActiveDays() {
			const rules = (this.instance && this.instance.timing_rules) || [];
			return rules.filter((r) => r.ranges && r.ranges.length > 0).length;
		},

		guaranteedTotalRanges() {
			const rules = (this.instance && this.instance.timing_rules) || [];
			return rules.reduce((sum, r) => sum + (r.ranges ? r.ranges.length : 0), 0);
		},

		minDate() {
			return moment().format('YYYY-MM-DD');
		},

		weekDays() {
			return [
				{ key: 'mon', label: 'Mon' },
				{ key: 'tue', label: 'Tue' },
				{ key: 'wed', label: 'Wed' },
				{ key: 'thu', label: 'Thu' },
				{ key: 'fri', label: 'Fri' },
				{ key: 'sat', label: 'Sat' },
				{ key: 'sun', label: 'Sun' },
			];
		},

		startDate: {
			get() {
				if (!this.instance || !this.instance.time_period) return null;
				return this.instance.time_period.start || null;
			},
			set(val) {
				var tp = (this.instance && this.instance.time_period) ? this.instance.time_period : { start: null, end: null };
				this.$set(this.instance, 'time_period', { ...tp, start: val });
			},
		},

		endDate: {
			get() {
				if (!this.instance || !this.instance.time_period) return null;
				return this.instance.time_period.end || null;
			},
			set(val) {
				var tp = (this.instance && this.instance.time_period) ? this.instance.time_period : { start: null, end: null };
				this.$set(this.instance, 'time_period', { ...tp, end: val });
			},
		},

		startTimeFormatted() {
			if (!this.instance || !this.instance.start_time) {
				return '00:00';
			}

			return this.instance.start_time;
		},

		endTimeFormatted() {
			if (!this.instance || !this.instance.end_time) {
				return '23:59';
			}

			return this.instance.end_time;
		},

		adFlight() {
			if (!this.instance.time_period || !this.instance.time_period.start || !this.instance.time_period.end) {
				return null;
			}
			const start = moment(this.instance.time_period.start);
			const end = moment(this.instance.time_period.end);
			return end.diff(start, 'days') + 1;
		},

		playbackRules() {
			return {
				content_show_count: this.instance.content_show_count,
				playback_order: this.instance.playback_order,
			};
		},

		isAnyRequredFieldEmpty() {
			return this.requiredFields.some(field => {
				let result = !this.instance[field];

				if (field === 'time_period') {
					let tp = this.instance.time_period;
					result = !(tp !== null && typeof tp === 'object' && tp.start && tp.end);
				}

				return result;
			});

		},
	}, // END: computed

	methods: {
		getDateFormat,

		toggleDay(day) {
			if (!this.instance.schedule) {
				this.$set(this.instance, 'schedule', { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false });
			}
			this.$set(this.instance.schedule, day, !this.instance.schedule[day]);
		},

		updateStartTime(val) {
			if (!val) return;

			this.$set(this.instance, 'start_time', val);
		},

		updateEndTime(val) {
			if (!val) return;

			this.$set(this.instance, 'end_time', val);
		},

		onTimingRulesInput(rules) {
			this.$set(this.instance, 'timing_rules', rules);
		},

		onPlaybackRulesInput(rules) {
			this.$set(this.instance, 'content_show_count', rules.content_show_count);
			this.$set(this.instance, 'playback_order', rules.playback_order);
		},

		onBookWithoutContentInput(checked) {
			this.$set(this.instance, 'book_without_content', !!checked);
		},

		/**
		 * We need to decide to show the whole block or not based on the data inside `DeviceChoices`
		 */
		onDeviceChoicesListsLoaded(lists) {
			if (
				lists.environmentList.length <= 1
				&& lists.resolutionList.length <= 1
				&& lists.broadcatsTypesList.length <= 1
			) {
				this.showDeviceChoices = false;
			}
			else {
				this.showDeviceChoices = true;
			}
		},
	},  // END: methods
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$border: $v2-border
$card: $v2-card-bg
$text: $v2-text
$muted: $v2-text-muted
$bg: #f3f4f6
$font: $v2-font
$r-card: 16px
$r-input: 12px
$shadow: $v2-shadow-card

// ── Root ───────────────────────────
.v2t
	display: flex
	flex-direction: column
	gap: 16px
	font-family: $font

// ── Card ───────────────────────────
.v2t__card
	background: $card
	border: 1px solid rgba($border, 0.6)
	border-radius: $r-card
	padding: 20px
	box-shadow: $shadow
	position: relative

	&--accent
		&::before
			content: ''
			position: absolute
			left: 0
			top: 18px
			bottom: 18px
			width: 3px
			border-radius: 0 3px 3px 0
			background: $purple

// ── Section Header ─────────────────
.v2t__section-header
	display: flex
	align-items: flex-start
	gap: 12px
	margin-bottom: 20px

.v2t__section-icon
	height: 40px
	width: 40px
	border-radius: 12px
	display: flex
	align-items: center
	justify-content: center
	background: rgba($teal, 0.1)
	color: $teal
	flex-shrink: 0

	svg
		width: 16px
		height: 16px

	&--accent
		background: rgba($purple, 0.1)
		color: $purple

.v2t__section-title
	font-size: 14px
	font-weight: 700
	color: $text
	line-height: 1.2
	margin: 0

.v2t__section-sub
	font-size: 12px
	color: $muted
	margin: 2px 0 0

// ── Hero Banner ────────────────────
.v2t__hero
	border: 1px solid rgba($teal, 0.2)
	background: linear-gradient(to right, rgba($teal, 0.05), rgba($teal, 0.03), transparent)
	border-radius: $r-card
	padding: 16px
	display: flex
	align-items: center
	gap: 12px
	flex-wrap: wrap

	&--purple
		border-color: rgba($purple, 0.25)
		background: linear-gradient(135deg, rgba($purple, 0.1), rgba($purple, 0.03) 60%, rgba($teal, 0.04))

		.v2t__hero-icon
			background: rgba($purple, 0.15)
			color: $purple

.v2t__hero-icon
	height: 36px
	width: 36px
	border-radius: 12px
	background: rgba($teal, 0.1)
	color: $teal
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 16px
		height: 16px

.v2t__hero-pill
	display: inline-flex
	align-items: center
	padding: 5px 12px
	border-radius: 9999px
	background: $purple
	color: #fff
	font-size: 11px
	font-weight: 700
	letter-spacing: 0.04em
	text-transform: uppercase

.v2t__hero-text
	font-size: 14px
	font-weight: 500
	color: $text

.v2t__hero-select
	height: 32px
	min-width: 120px
	padding: 0 28px 0 12px
	border: 1px solid rgba($teal, 0.3)
	background: $bg
	border-radius: 10px
	font-size: 12px
	font-weight: 600
	color: $text
	appearance: none
	font-family: $font
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
	background-repeat: no-repeat
	background-position: right 8px center

.v2t__hero-model
	font-size: 14px
	font-weight: 700
	color: $teal

.v2t__hero-hint
	margin-left: auto
	font-size: 11px
	color: $muted
	display: inline-flex
	align-items: center
	gap: 4px

	svg
		width: 12px
		height: 12px

// ── Form elements ──────────────────
.v2t__label
	display: block
	font-size: 10px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $muted
	margin-bottom: 6px

.v2t__input-wrap
	position: relative

	&--sm
		width: 128px
		flex-shrink: 0

	&--xs
		width: 100px
		flex-shrink: 0

	&--duration
		width: 100px
		flex-shrink: 0

	&--freq
		width: 80px
		flex-shrink: 0

	&--budget
		max-width: 280px

.v2t__input
	width: 100%
	height: 40px
	padding: 0 12px
	background: rgba($bg, 0.5)
	border: 1px solid $border
	border-radius: $r-input
	font-size: 13px
	font-family: $font
	color: $text
	transition: all 0.15s
	-moz-appearance: textfield

	&--has-suffix
		padding-right: 36px

	&--has-suffix-sm
		padding-right: 44px

	&::-webkit-inner-spin-button,
	&::-webkit-outer-spin-button
		-webkit-appearance: none

	&:focus
		outline: none
		border-color: $teal
		background: $card
		box-shadow: 0 0 0 3px rgba($teal, 0.12)

	&--error
		border-color: $v2-red

	&:disabled
		opacity: 0.6
		cursor: not-allowed

.v2t__input-suffix
	position: absolute
	right: 12px
	top: 50%
	transform: translateY(-50%)
	font-size: 14px
	font-weight: 700
	color: $muted
	pointer-events: none

.v2t__input-suffix-sm
	position: absolute
	right: 12px
	top: 50%
	transform: translateY(-50%)
	font-size: 10px
	font-weight: 600
	color: $muted
	text-transform: uppercase
	pointer-events: none

.v2t__select
	height: 40px
	padding: 0 32px 0 16px
	background: rgba($bg, 0.5)
	border: 1px solid $border
	border-radius: $r-input
	font-size: 13px
	font-family: $font
	color: $text
	appearance: none
	box-sizing: border-box
	min-width: 140px
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
	background-repeat: no-repeat
	background-position: right 10px center
	transition: all 0.15s

	&:focus
		outline: none
		border-color: $teal
		background: $card
		box-shadow: 0 0 0 3px rgba($teal, 0.12)

.v2t__help
	font-size: 11px
	color: $muted
	margin-top: 6px

	b
		color: $text

	&--error
		color: $v2-red
		font-weight: 500

	&--mt
		margin-top: 20px

.v2t__help-inline
	font-size: 11px
	color: $muted
	white-space: nowrap

// ── Layout helpers ─────────────────
.v2t__grid-2
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 16px

	@media (max-width: 768px)
		grid-template-columns: 1fr

.v2t__stack
	display: flex
	flex-direction: column
	gap: 16px

// ── Schedule columns ───────────────
.v2t__schedule-cols
	display: flex
	align-items: flex-start
	gap: 0

.v2t__schedule-col
	flex: none
	width: 140px
	display: flex
	flex-direction: column
	gap: 12px

.v2t__schedule-sep
	color: $muted
	font-size: 16px
	padding: 28px 12px 0
	flex-shrink: 0

.v2t__label--mt
	margin-top: 16px

.v2t__input--time
	width: 100%
	max-width: 140px
	box-sizing: border-box

// ── Schedule extras ────────────────
.v2t__weekdays
	margin-top: 20px

.v2t__pill-group
	display: flex
	gap: 6px
	flex-wrap: wrap

.v2t__pill
	height: 36px
	padding: 0 16px
	border-radius: 9999px
	font-size: 12px
	font-weight: 600
	font-family: $font
	background: $bg
	color: $muted
	border: 1px solid $border
	cursor: pointer
	transition: all 0.15s

	&:hover
		border-color: rgba($teal, 0.4)
		color: $text

	&--active
		background: $teal
		color: #fff
		border-color: $teal
		box-shadow: $shadow

// ── Ad Duration & Frequency ─────────
.v2t__adfreq
	display: flex
	gap: 40px
	flex-wrap: wrap

.v2t__adfreq-col
	min-width: 0

.v2t__freq-inputs
	display: inline-flex
	align-items: center

	.v2t__input-wrap--freq
		margin-right: 32px

// ── Budget: fixed cost box ─────────
.v2t__fixed-cost-box
	background: rgba($bg, 0.5)
	border-radius: $r-input
	padding: 16px

.v2t__fixed-cost-value
	font-size: 24px
	font-weight: 700
	color: $text
	margin: 4px 0

.v2t__fixed-cost-currency
	font-size: 14px
	font-weight: 400
	color: $muted
	margin-left: 4px

// ── Toggle box ─────────────────────
.v2t__toggle-box
	border: 1px solid $border
	border-radius: $r-input
	padding: 16px
	transition: all 0.2s

	&--on
		border-color: rgba($teal, 0.4)
		background: rgba($teal, 0.05)

.v2t__toggle-row
	display: flex
	align-items: flex-start
	justify-content: space-between
	gap: 12px

.v2t__toggle-title
	font-size: 12px
	font-weight: 600
	color: $text

.v2t__toggle-desc
	font-size: 11px
	color: $muted
	margin-top: 2px

// ── Switch ─────────────────────────
.v2t__switch
	position: relative
	display: inline-block
	width: 36px
	height: 20px
	flex-shrink: 0

	input
		opacity: 0
		width: 0
		height: 0

.v2t__switch-slider
	position: absolute
	cursor: pointer
	top: 0
	left: 0
	right: 0
	bottom: 0
	background: $border
	border-radius: 9999px
	transition: background 0.2s

	&::before
		content: ''
		position: absolute
		height: 16px
		width: 16px
		left: 2px
		bottom: 2px
		background: white
		border-radius: 50%
		transition: transform 0.2s
		box-shadow: 0 1px 3px rgba(0,0,0,0.2)

	input:checked + &
		background: $teal

	input:checked + &::before
		transform: translateX(16px)

// ── Map grid ───────────────────────
.v2t__map-grid
	display: grid
	grid-template-columns: 2fr 3fr
	gap: 16px

	@media (max-width: 1024px)
		grid-template-columns: 1fr

.v2t__address-card
	display: flex
	flex-direction: column

.v2t__map-wrap
	border-radius: $r-card
	overflow: hidden
	border: 1px solid rgba($border, 0.6)
	box-shadow: $shadow
	height: 420px
	position: relative

.v2t__map
	position: absolute
	top: 0
	left: 0
	right: 0
	bottom: 0

	::v-deep
		// Map wrapper and canvas — fill container
		.mgl-map-wrapper
			width: 100% !important
			height: 100% !important
			position: absolute !important
			top: 0
			left: 0

		.mgl-map-wrapper > div
			width: 100% !important
			height: 100% !important

		.map-container,
		.mapboxgl-map,
		.maplibregl-map,
		.mapboxgl-canvas-container,
		.mapboxgl-canvas
			width: 100% !important
			height: 100% !important

		// Geocoder search — hide (we have SelectAreas)
		.mapboxgl-ctrl-geocoder
			display: none !important

		// Controls styling
		.mapboxgl-ctrl-group
			border-radius: 8px
			box-shadow: 0 1px 4px rgba(0,0,0,0.15)

		// Hide attribution
		.mapboxgl-ctrl-bottom-left,
		.mapboxgl-ctrl-bottom-right
			display: none

		.metric-input
			min-width: 80px
			max-width: none

		.selected_area-delete
			margin-left: 5px

// ── Device Choices override ────────
.v2t__device-choices
	::v-deep
		&.data_block
			margin: 0
			padding: 0

		h3
			font-size: 13px
			font-weight: 600
			color: $text
			margin-top: 0
			margin-bottom: 12px

// ── Platforms ──────────────────────
.v2t__platforms-spoiler
	width: 100%

// ── Summary Card ───────────────────
.v2t__summary-card
	border: 1px solid rgba($border, 0.6)
	background: linear-gradient(135deg, $card, $card, rgba($teal, 0.03))
	border-radius: $r-card
	padding: 20px
	box-shadow: $shadow

.v2t__stats-grid
	display: grid
	grid-template-columns: repeat(6, 1fr)
	gap: 12px

	@media (max-width: 1024px)
		grid-template-columns: repeat(3, 1fr)

	@media (max-width: 640px)
		grid-template-columns: repeat(2, 1fr)

.v2t__stat
	background: rgba($bg, 0.8)
	border: 1px solid rgba($border, 0.4)
	border-radius: $r-input
	padding: 12px
	text-align: center

.v2t__stat-label
	font-size: 9px
	text-transform: uppercase
	letter-spacing: 0.06em
	font-weight: 600
	color: $muted

.v2t__stat-value
	margin-top: 4px
	font-size: 20px
	font-weight: 700
	color: $text
	font-family: 'JetBrains Mono', monospace
	letter-spacing: -0.02em

	&--text
		font-size: 14px
		font-weight: 600
		font-family: $font
		letter-spacing: 0

	&--primary
		color: $teal

	&--accent
		color: $purple

	&--danger
		color: $v2-red

.v2t__stat-value-unit
	font-size: 11px
	font-weight: 600
	color: $muted
	margin-left: 4px
	font-family: $font

// ── Summary CTA ────────────────────
.v2t__summary-cta
	margin-top: 16px
	display: flex
	justify-content: center

// ── Buttons ────────────────────────
.v2t__btn
	display: inline-flex
	align-items: center
	justify-content: center
	gap: 6px
	height: 36px
	padding: 0 16px
	border-radius: $r-input
	font-size: 12px
	font-weight: 600
	font-family: $font
	transition: all 0.15s
	border: 1px solid transparent
	cursor: pointer

	svg
		width: 14px
		height: 14px

	&--outline
		background: $bg
		border-color: $border
		color: $text

		&:hover
			background: rgba($border, 0.5)

		&:disabled
			opacity: 0.5
			cursor: not-allowed

// ── Validation ─────────────────────
.v2t__validation
	display: flex
	gap: 12px
	padding: 12px 16px
	background: rgba($v2-red, 0.05)
	border: 1px solid rgba($v2-red, 0.2)
	border-radius: $r-input
	font-size: 12px

.v2t__validation-msg
	color: $v2-red
	font-weight: 500

// ── Inventory Modal ────────────────
.v2t__inventory-modal
	::v-deep > *
		max-width: 1320px

// ── NiceDatetime override ──────────
.v2t__datetime
	width: 100%
	max-width: 140px
	border: none !important
	border-bottom: none !important

	::v-deep
		.field-input
			border: 1px solid $border !important
			border-radius: $r-input !important
			background: rgba($bg, 0.5) !important
			font-size: 13px !important
			height: 40px !important
			padding: 0 12px !important
			width: 100% !important

			&:focus
				border-color: $teal !important
				box-shadow: 0 0 0 3px rgba($teal, 0.12) !important

		.custom-button-effect
			display: none !important

// ── TimeRange override ─────────────
.v2t__time-range
	::v-deep
		.nice-range
			padding: 0

		.time
			display: flex
			align-items: flex-end
			gap: 8px

		.time > div
			flex: 1
			min-width: 0

		.time-input
			border: 1px solid $border
			border-radius: $r-input
			background: rgba($bg, 0.5)
			font-size: 13px
			height: 40px
			padding: 0 12px
			font-family: $font
			color: $text
			width: 100%
			box-sizing: border-box

			&:focus
				outline: none
				border-color: $teal
				background: $card
				box-shadow: 0 0 0 3px rgba($teal, 0.12)

		.separator
			color: $muted
			font-size: 16px
			padding-bottom: 8px
			flex-shrink: 0

		.nice-label
			font-size: 10px !important
			font-weight: 600 !important
			text-transform: uppercase !important
			letter-spacing: 0.06em !important
			color: $muted !important

// ── SelectAreas override ───────────
.v2t__select-areas
	::v-deep
		.selected_area
			align-items: center

		.selected_area-action_icon
			display: none

		.selected_area-address
			margin-left: 15px

		.selected_area-radius
			width: auto
			min-width: 80px
			margin-left: 15px

		.selected_area-delete
			margin-left: 5px

		.selected_area-toggle_action::after
			display: none

		.ni_input--input
			border: 1px solid $border !important
			border-radius: $r-input !important
			background: rgba($bg, 0.5) !important
			font-size: 13px !important
			height: 40px !important

		.tags .tag
			background: rgba($teal, 0.1)
			color: $teal
			border-radius: 9999px
			font-size: 11px
			font-weight: 500
</style>
